import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { API, get, post, put } from 'utils/api';
import { logError } from 'utils/log';
import { isCcmCampaignTimeline } from 'modules/campaigns/components/ccm/utils/ccm-timeline';
import {
  mapCampaignToCreatorlessCampaign,
  mapCreatorlessCampaignToCcmCampaign
} from '../utils';

import {
  Campaign,
  Dispatch as CommonDispatch
} from 'types';
import {
  CreatorlessCampaign,
  CcmCampaignFormCreatorlessDispatch as Dispatch
} from '../types';

export function flush(): (d: Dispatch) => Promise<void> {
  return async dispatch => {
    dispatch({ type: 'creatorless-campaign-flush' });
  };
}

async function fetchRealmOutfitsRegions(): Promise<Array<string>> {
  try {
    return (await get(API.END_POINTS.REALM_OUTFITS.getOutfitsRegions()))?.data ?? [];
  } catch (error) {
    logError(error);

    return [];
  }
}

export function init(campaign: null | Campaign): (d: Dispatch) => Promise<void> {
  return async dispatch => {
    const regions = await fetchRealmOutfitsRegions();

    dispatch({
      type: 'creatorless-campaign-regions-update',
      payload: {
        regions
      }
    });

    if (campaign === null) {
      return;
    }

    const formResult = mapCampaignToCreatorlessCampaign(campaign);

    if (formResult.type === 'error') {
      logError(new Error(formResult.error));

      return;
    }

    dispatch({
      type: 'creatorless-campaign-form-from-campaign-update',
      payload: {
        form: formResult.value
      }
    });
  };
}

export function handleGoLiveDateChange(date: string): (d: Dispatch) => Promise<void> {
  return async dispatch => {
    dispatch({ type: 'creatorless-campaign-go-live-date-update', payload: { date } });
    try {
      const url = API.END_POINTS.CCM.generateTimeline(['go-live-date', date]);
      const timeline = (await get(url))?.data ?? null;

      if (!isCcmCampaignTimeline(timeline)) {
        throw new Error(`Invalid response for start date: ${date}`);
      }

      dispatch({ type: 'creatorless-campaign-timeline-update', payload: { timeline } });
    } catch (error) {
      logError(error);
    }
  };
}

export function createCcmCreatorlessCampaign(
  brandProfileId: number,
  campaign: CreatorlessCampaign,
  onSuccess: (campaignId: number) => void
): (d: CommonDispatch) => Promise<void> {
  const url = API.END_POINTS.CCM.createCampaign(brandProfileId);
  return async dispatch => {
    try {
      const mapResult = mapCreatorlessCampaignToCcmCampaign(campaign);

      if (mapResult.type === 'error') {
        throw new Error(mapResult.error);
      }

      dispatch(showLoading(url));
      const result = (await post(url, { data: mapResult.value }))?.data ?? null;

      if (result === null) {
        throw new Error(`Creation of a CCM Campaign for brandd with id: ${brandProfileId} has failed`);
      }

      dispatch({ type: 'creatorless-campaign-flush' });
      onSuccess(result.id);
      dispatch(hideLoading(url));
    } catch (error) {
      logError(error);
      dispatch(hideLoading(url));
      dispatch({
        type: 'creatorless-campaign-form-submittion-state-update',
        payload: {
          submittionState: {
            type: 'error',
            message: error?.response?.data?.detail ?? 'Unknown error. Please try again'
          }
        }
      });
    }
  };
}

export function updateCcmCreatorlessCampaign(
  campaignId: number,
  campaign: CreatorlessCampaign,
  onSuccess: (campaignId: number) => void
): (d: CommonDispatch) => Promise<void> {
  const url = API.END_POINTS.CCM.updateCampaign(campaignId);
  return async dispatch => {
    try {
      const mapResult = mapCreatorlessCampaignToCcmCampaign(campaign);

      if (mapResult.type === 'error') {
        throw new Error(mapResult.error);
      }

      dispatch(showLoading(url));
      await put(url, { data: mapResult.value });

      dispatch({ type: 'creatorless-campaign-flush' });
      onSuccess(campaignId);
      dispatch(hideLoading(url));
    } catch (error) {
      logError(error);
      dispatch(hideLoading(url));
      dispatch({
        type: 'creatorless-campaign-form-submittion-state-update',
        payload: {
          submittionState: {
            type: 'error',
            message: error?.response?.data?.detail ?? 'Unknown error. Please try again'
          }
        }
      });
    }
  };
}
